import { type PointerEvent, useEffect, useRef } from 'react'

import { Icon } from '@/component/atoms/Icon'
import modalService from '@/service/modal.service'
import navigationUtils from '@/utils/navigationUtils'

export const LONG_PRESS_THRESHOLD_MS = 800

const RefreshButton = () => {
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const suppressNextClickRef = useRef(false)

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => clearTimer, [])

  // 멀티터치의 두 번째 손가락도 button 이 0 이라 isPrimary 로 걸러야 한다.
  // undefined 는 통과시킨다 — 값이 없는 환경에서 제스처가 통째로 죽는 쪽이 더 나쁘다.
  const startLongPress = ({ button, isPrimary }: PointerEvent<HTMLButtonElement>) => {
    if (button !== 0 || isPrimary === false) return

    // 롱프레스 뒤 click 이 버튼에 도달하지 않는 경우(모달이 즉시 덮는 실기기 케이스)에도
    // 억제 표시가 다음 조작까지 남아 새로고침을 삼키지 않도록 누를 때마다 초기화한다.
    suppressNextClickRef.current = false
    clearTimer()

    timerRef.current = setTimeout(() => {
      timerRef.current = null
      suppressNextClickRef.current = true
      modalService.openReleaseIdModal()
    }, LONG_PRESS_THRESHOLD_MS)
  }

  // 터치 포인터는 implicit pointer capture 로 pointerleave 가 pointerup 이후에야 오므로,
  // 누르는 중 버튼을 벗어났는지는 좌표로만 판정할 수 있다.
  const cancelWhenMovedOutside = ({
    currentTarget,
    clientX,
    clientY,
  }: PointerEvent<HTMLButtonElement>) => {
    if (!timerRef.current) return

    const { left, top, right, bottom } = currentTarget.getBoundingClientRect()
    if (clientX < left || clientX > right || clientY < top || clientY > bottom) {
      clearTimer()
    }
  }

  return (
    <button
      data-component="새로고침버튼"
      className="flex handler-button"
      onPointerDown={startLongPress}
      onPointerMove={cancelWhenMovedOutside}
      onPointerUp={clearTimer}
      onPointerLeave={clearTimer}
      onPointerCancel={clearTimer}
      // 키보드 조작은 pointerdown 을 거치지 않아, 남아 있는 억제 표시가 Enter 한 번을 삼킨다.
      onKeyDown={() => {
        suppressNextClickRef.current = false
      }}
      onClick={() => {
        if (suppressNextClickRef.current) {
          suppressNextClickRef.current = false
          return
        }
        navigationUtils.reload()
      }}
    >
      <div className="icon__wrapper button__refresh">
        <Icon name="refresh" size={20} color="var(--Colors-Gray-700)" />
      </div>
    </button>
  )
}

export default RefreshButton
